import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { format } from 'domains/dates/format';
import { Card } from 'components/Card';
import { GoalStatusToken } from 'components/GoalSection';
import { GoalChange } from 'components/GoalSection/GoalChange';
import { Icon } from 'components/Icon';
import { Heading, Paragraph } from 'components/Typography';
import { ShortTermGoal, SessionProgress } from 'domains/care-plans';
import { DATE_SHORT_MONTH } from 'utils/date';
import { HelpText } from 'components/HelpText';
import { calculateGoalProgress, formatGoalTarget } from 'domains/goals/goals';

type Props = {
  showDivider?: boolean;
  shortTermGoal: ShortTermGoal;
  pdf?: boolean;
};

export const CarePlanShortTermGoalSection = ({ showDivider = true, shortTermGoal, pdf }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { goalDescription, goalStatus, sessionProgress } = shortTermGoal;

  return (
    <div className="grid grid-cols-[40px_1fr] gap-x-24">
      <div className="bg-purple-200 rounded-full m-4 shrink-0 w-32 h-32 flex items-center justify-center">
        <Icon name="Flag" className="w-20 h-20" />
      </div>

      <div
        data-testid="care-plan-short-term-goal-section-dashboard-carePlanGoals-shortTermGoal-heading"
        className="flex flex-col sm:flex-row items-start sm:items-center gap-8 sm:h-full"
      >
        <Heading level="h5" as="h4" className="font-semibold">
          {t('dashboard.carePlanGoals.shortTermGoal.heading')}
        </Heading>

        <GoalStatusToken status={goalStatus} />
      </div>

      <div className="flex flex-col items-center py-8">
        {showDivider && (
          <div
            className={cx('border-l-[3px] border-purple-200 grow', {
              'border-transparent': pdf,
            })}
          />
        )}
      </div>

      <div
        data-testid="care-plan-short-term-goal-section-goal-section"
        className={cx('mt-12 sm:mt-16', {
          'pb-32': showDivider,
          'break-inside-avoid break-before-avoid': pdf,
        })}
      >
        <Paragraph>{goalDescription}</Paragraph>
        {!pdf && (
          <HelpText
            text={t('dashboard.carePlanGoals.whatIsShortTermGoal')}
            tooltipText={t('dashboard.carePlanGoals.shortTermGoal.explanation')}
          />
        )}
        {sessionProgress && (
          <SessionProgressCard
            className="mt-24 break-inside-avoid"
            shortTermGoal={shortTermGoal}
            sessionProgress={sessionProgress}
          />
        )}
      </div>
    </div>
  );
};

type SessionProgressCardProps = {
  className?: string;
  shortTermGoal: ShortTermGoal;
  sessionProgress: SessionProgress;
};

const SessionProgressCard = ({ className, shortTermGoal, sessionProgress }: SessionProgressCardProps): JSX.Element => {
  const { t } = useTranslation();
  const { type, goalBaseline, goalBaselineContext, goalBaselineDate, goalContext, goalDueDate, goalTarget } =
    shortTermGoal;
  const { levelInSession, currentLevelDate, previousLevelComparison, cues } = sessionProgress;
  const goalProgress = calculateGoalProgress(goalBaseline, goalTarget, levelInSession);

  return (
    <Card className={cx(className, 'text-center border border-black-200 rounded-md p-24 md:p-24')}>
      <p className="text-gray-600 text-sm leading-none">
        {t('dashboard.carePlanGoals.shortTermGoal.levelHeading', {
          date: format(new Date(currentLevelDate), DATE_SHORT_MONTH),
        })}
      </p>
      <p className="text-3xl font-bold flex items-center justify-center mt-8 md:mt-12">
        {formatGoalTarget(levelInSession, type)}
        {previousLevelComparison !== undefined && <GoalChange amount={previousLevelComparison} className="ml-8" />}
      </p>

      <p className="text-sm font-semibold md:mt-8">{cues}</p>
      {goalProgress && (
        <div className="bg-orange-50 h-8 md:h-16 rounded-sm overflow-hidden mt-20 md:mt-24">
          <div className="h-full bg-orange-300" style={{ width: `${goalProgress}%` }} />
        </div>
      )}
      <div className="flex justify-between mt-16 md:mt-24">
        <div className="text-left text-xs md:text-sm">
          <p className="text-gray-600">
            {t('dashboard.carePlanGoals.shortTermGoal.baselineLabel')}{' '}
            {goalBaselineDate && (
              <span className="sm-d:hidden">{format(new Date(goalBaselineDate), DATE_SHORT_MONTH)}</span>
            )}
          </p>

          <p className="text-lg md:text-xl font-semibold">{formatGoalTarget(goalBaseline, type)}</p>
          <p className="sm-d:hidden text-sm font-semibold">{goalBaselineContext}</p>
        </div>
        <div className="text-right text-xs md:text-sm">
          <p className="text-gray-600">
            {t('dashboard.carePlanGoals.shortTermGoal.targetLabel')}{' '}
            {goalDueDate && <span className="sm-d:hidden">{format(new Date(goalDueDate), DATE_SHORT_MONTH)}</span>}
          </p>

          <p className="text-lg md:text-xl font-semibold">{formatGoalTarget(goalTarget, type)}</p>
          <p className="sm-d:hidden text-sm font-semibold">{goalContext}</p>
        </div>
      </div>
    </Card>
  );
};
